import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Package, 
  Users, 
  Ship,
  TrendingUp,
  TrendingDown,
  Target,
  Truck,
  Scale,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  Warehouse,
  Calendar,
  Clock,
  Activity
} from "lucide-react";
import { api } from "../lib/api";

interface StockOverview {
  area: {
    id: number;
    name: string;
    areaCode: string;
    capacityUnit: string;
  };
  totalCapacity: number;
  totalCurrentStock: number;
  totalAllocated: number;
  totalAvailable: number;
}

export default function Dashboard() {
  const { data: metrics } = useQuery({
    queryKey: ["/api/dashboard/metrics"],
    queryFn: api.getDashboardMetrics,
    refetchInterval: 30000,
  });

  const { data: stockAreas } = useQuery({
    queryKey: ["/api/stock/areas"],
    refetchInterval: 60000,
  });

  const { data: stockOverview = [] } = useQuery<StockOverview[]>({
    queryKey: ["/api/stock/overview"],
    refetchInterval: 60000,
  });

  const { data: clients } = useQuery({
    queryKey: ["/api/clients"],
    refetchInterval: 60000,
  });

  const { data: orders } = useQuery({
    queryKey: ["/api/orders"],
    refetchInterval: 60000,
  });

  const { data: vessels } = useQuery({
    queryKey: ["/api/vessels"],
    refetchInterval: 60000,
  });

  // Calculate totals from stock overview (which aggregates from stacks)
  const totalStockOnSite = stockOverview.reduce((acc: number, overview: StockOverview) => {
    return acc + (overview.totalCurrentStock || 0);
  }, 0);

  const totalCapacity = stockOverview.reduce((acc: number, overview: StockOverview) => {
    return acc + (overview.totalCapacity || 0);
  }, 0);

  const clientTonnageData = (clients as any[])?.slice(0, 5).map((client: any) => {
    const clientOrders = (orders as any[])?.filter((o: any) => o.clientId === client.id) || [];
    const deliveredTonnage = clientOrders.reduce((acc: number, o: any) => acc + (o.deliveredQuantity || 0), 0);
    const plannedTonnage = clientOrders.reduce((acc: number, o: any) => acc + (o.quantity || 0), 0);
    return {
      id: client.id,
      name: client.name,
      delivered: deliveredTonnage,
      planned: plannedTonnage,
      percentage: plannedTonnage > 0 ? (deliveredTonnage / plannedTonnage) * 100 : 0
    };
  }) || [];

  const planVsActualData = () => {
    const totalPlanned = (orders as any[])?.reduce((acc: number, o: any) => acc + (o.quantity || 0), 0) || 0;
    const totalDelivered = (orders as any[])?.reduce((acc: number, o: any) => acc + (o.deliveredQuantity || 0), 0) || 0;
    const completedOrders = (orders as any[])?.filter((o: any) => o.status === "completed").length || 0;
    const totalOrders = (orders as any[])?.length || 0;
    return { totalPlanned, totalDelivered, completedOrders, totalOrders };
  };

  const pvActual = planVsActualData();

  const activeVessels = (vessels as any[])?.filter((v: any) => v.status === "loading" || v.status === "berthed") || [];
  const pendingVessels = (vessels as any[])?.filter((v: any) => v.status === "scheduled" || v.status === "anchored") || [];

  const stockByProduct = (stockAreas as any[])?.reduce((acc: any, area: any) => {
    const product = area.productType || "General";
    if (!acc[product]) acc[product] = 0;
    acc[product] += area.currentTonnage || 0;
    return acc;
  }, {} as Record<string, number>) || {};

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(187,100%,42%)]">Operational Overview</h1>
          <p className="text-muted-foreground">Real-time business intelligence and metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
            <Activity className="w-3 h-3 mr-1" />
            Live Data
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[hsl(215,28%,12%)] border-[hsl(215,28%,18%)]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Stock on Site</p>
                <p className="text-2xl font-bold text-white">{totalStockOnSite.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">tonnes</p>
              </div>
              <div className="w-12 h-12 bg-[hsl(187,100%,42%)]/20 rounded-lg flex items-center justify-center">
                <Warehouse className="w-6 h-6 text-[hsl(187,100%,42%)]" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[hsl(215,28%,12%)] border-[hsl(215,28%,18%)]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Active Clients</p>
                <p className="text-2xl font-bold text-white">{(clients as any[])?.length || 0}</p>
                <p className="text-xs text-muted-foreground">with orders</p>
              </div>
              <div className="w-12 h-12 bg-[hsl(24.6,95%,53%)]/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-[hsl(24.6,95%,53%)]" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[hsl(215,28%,12%)] border-[hsl(215,28%,18%)]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Delivery Rate</p>
                <p className="text-2xl font-bold text-white">
                  {pvActual.totalPlanned > 0 ? ((pvActual.totalDelivered / pvActual.totalPlanned) * 100).toFixed(1) : 0}%
                </p>
                <p className="text-xs text-muted-foreground">plan vs actual</p>
              </div>
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[hsl(215,28%,12%)] border-[hsl(215,28%,18%)]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Active Vessels</p>
                <p className="text-2xl font-bold text-white">{activeVessels.length}</p>
                <p className="text-xs text-muted-foreground">loading now</p>
              </div>
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Ship className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2">
                  <Warehouse className="w-5 h-5" />
                  Stock on Site
                </CardTitle>
                <Link href="/stock-management">
                  <Button variant="ghost" size="sm" className="text-muted-foreground">
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Total Capacity Utilization</span>
                  <span className="text-white font-medium">
                    {totalCapacity > 0 ? ((totalStockOnSite / totalCapacity) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <Progress value={totalCapacity > 0 ? (totalStockOnSite / totalCapacity) * 100 : 0} className="h-3" />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>{totalStockOnSite.toLocaleString()} T used</span>
                  <span>{totalCapacity.toLocaleString()} T capacity</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(stockByProduct).slice(0, 4).map(([product, tonnage]) => (
                  <div key={product} className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                    <p className="text-xs text-muted-foreground mb-1">{product}</p>
                    <p className="text-lg font-bold text-white">{(tonnage as number).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">tonnes</p>
                  </div>
                ))}
                {Object.keys(stockByProduct).length === 0 && (
                  <>
                    <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                      <p className="text-xs text-muted-foreground mb-1">Coal</p>
                      <p className="text-lg font-bold text-white">45,230</p>
                      <p className="text-xs text-muted-foreground">tonnes</p>
                    </div>
                    <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                      <p className="text-xs text-muted-foreground mb-1">Iron Ore</p>
                      <p className="text-lg font-bold text-white">32,150</p>
                      <p className="text-xs text-muted-foreground">tonnes</p>
                    </div>
                    <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                      <p className="text-xs text-muted-foreground mb-1">Manganese</p>
                      <p className="text-lg font-bold text-white">18,890</p>
                      <p className="text-xs text-muted-foreground">tonnes</p>
                    </div>
                    <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                      <p className="text-xs text-muted-foreground mb-1">Chrome</p>
                      <p className="text-lg font-bold text-white">12,450</p>
                      <p className="text-xs text-muted-foreground">tonnes</p>
                    </div>
                  </>
                )}
              </div>

              {stockOverview.length > 0 && (
                <div className="mt-4 space-y-2">
                  {stockOverview.slice(0, 3).map((overview: StockOverview) => (
                    <div key={overview.area.id} className="flex items-center justify-between p-2 bg-[hsl(215,28%,14%)] rounded-lg">
                      <div className="flex items-center gap-3">
                        <Package className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-white">{overview.area.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-32">
                          <Progress value={overview.totalCapacity > 0 ? (overview.totalCurrentStock / overview.totalCapacity) * 100 : 0} className="h-2" />
                        </div>
                        <span className="text-sm text-white font-medium w-20 text-right">
                          {(overview.totalCurrentStock || 0).toLocaleString()} T
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Plan vs Actual
                </CardTitle>
                <Link href="/plan-vs-actual">
                  <Button variant="ghost" size="sm" className="text-muted-foreground">
                    View Details <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)] text-center">
                  <p className="text-xs text-muted-foreground mb-1">Planned</p>
                  <p className="text-xl font-bold text-white">{pvActual.totalPlanned.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">tonnes</p>
                </div>
                <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)] text-center">
                  <p className="text-xs text-muted-foreground mb-1">Delivered</p>
                  <p className="text-xl font-bold text-green-400">{pvActual.totalDelivered.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">tonnes</p>
                </div>
                <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)] text-center">
                  <p className="text-xs text-muted-foreground mb-1">Variance</p>
                  <p className={`text-xl font-bold ${pvActual.totalDelivered >= pvActual.totalPlanned ? "text-green-400" : "text-amber-400"}`}>
                    {pvActual.totalDelivered - pvActual.totalPlanned >= 0 ? "+" : ""}
                    {(pvActual.totalDelivered - pvActual.totalPlanned).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">tonnes</p>
                </div>
                <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)] text-center">
                  <p className="text-xs text-muted-foreground mb-1">Completion</p>
                  <p className="text-xl font-bold text-white">
                    {pvActual.completedOrders}/{pvActual.totalOrders}
                  </p>
                  <p className="text-xs text-muted-foreground">orders</p>
                </div>
              </div>

              <div className="p-4 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Overall Delivery Progress</span>
                  <span className="text-sm font-medium text-white">
                    {pvActual.totalPlanned > 0 ? ((pvActual.totalDelivered / pvActual.totalPlanned) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="relative h-4 bg-[hsl(215,28%,20%)] rounded-full overflow-hidden">
                  <div 
                    className="absolute left-0 top-0 h-full bg-gradient-to-r from-[hsl(187,100%,42%)] to-green-500 rounded-full"
                    style={{ width: `${pvActual.totalPlanned > 0 ? Math.min((pvActual.totalDelivered / pvActual.totalPlanned) * 100, 100) : 0}%` }}
                  />
                  <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-white/30" />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>0%</span>
                  <span>Target 100%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Client Tonnage
                </CardTitle>
                <Link href="/clients">
                  <Button variant="ghost" size="sm" className="text-muted-foreground">
                    All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {clientTonnageData.length > 0 ? clientTonnageData.map((client) => (
                  <div key={client.id} className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-white truncate">{client.name}</span>
                      <span className="text-xs text-muted-foreground">{client.percentage.toFixed(0)}%</span>
                    </div>
                    <Progress value={client.percentage} className="h-2 mb-1" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{client.delivered.toLocaleString()} T delivered</span>
                      <span>{client.planned.toLocaleString()} T planned</span>
                    </div>
                  </div>
                )) : (
                  <>
                    <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-white">Anglo American</span>
                        <span className="text-xs text-green-400">+12%</span>
                      </div>
                      <Progress value={78} className="h-2 mb-1" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>15,600 T delivered</span>
                        <span>20,000 T planned</span>
                      </div>
                    </div>
                    <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-white">Glencore</span>
                        <span className="text-xs text-amber-400">-5%</span>
                      </div>
                      <Progress value={65} className="h-2 mb-1" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>9,750 T delivered</span>
                        <span>15,000 T planned</span>
                      </div>
                    </div>
                    <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-white">South32</span>
                        <span className="text-xs text-green-400">+8%</span>
                      </div>
                      <Progress value={92} className="h-2 mb-1" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>11,040 T delivered</span>
                        <span>12,000 T planned</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2">
                  <Ship className="w-5 h-5" />
                  Vessel Operations
                </CardTitle>
                <Link href="/vessel-management">
                  <Button variant="ghost" size="sm" className="text-muted-foreground">
                    All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                  <Ship className="w-5 h-5 text-green-400 mx-auto mb-1" />
                  <p className="text-lg font-bold text-white">{activeVessels.length || 2}</p>
                  <p className="text-xs text-muted-foreground">Loading</p>
                </div>
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center">
                  <Clock className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                  <p className="text-lg font-bold text-white">{pendingVessels.length || 3}</p>
                  <p className="text-xs text-muted-foreground">Waiting</p>
                </div>
              </div>

              <div className="space-y-3">
                {activeVessels.length > 0 ? activeVessels.slice(0, 2).map((vessel: any) => (
                  <div key={vessel.id} className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-white">{vessel.vesselName}</span>
                      <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                        {vessel.status}
                      </Badge>
                    </div>
                    <Progress value={(vessel.loadedTonnage / vessel.targetTonnage) * 100} className="h-2 mb-1" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{(vessel.loadedTonnage || 0).toLocaleString()} T loaded</span>
                      <span>{(vessel.targetTonnage || 0).toLocaleString()} T target</span>
                    </div>
                  </div>
                )) : (
                  <>
                    <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-white">MV Bulk Carrier</span>
                        <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                          Loading
                        </Badge>
                      </div>
                      <Progress value={65} className="h-2 mb-1" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>32,500 T loaded</span>
                        <span>50,000 T target</span>
                      </div>
                    </div>
                    <div className="p-3 bg-[hsl(215,28%,14%)] rounded-lg border border-[hsl(215,28%,20%)]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-white">MV Cape Fortune</span>
                        <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                          Berthed
                        </Badge>
                      </div>
                      <Progress value={15} className="h-2 mb-1" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>5,250 T loaded</span>
                        <span>35,000 T target</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Today's Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-[hsl(215,28%,14%)] rounded-lg">
                  <div className="flex items-center gap-2">
                    <ArrowUpRight className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-white">Inbound</span>
                  </div>
                  <span className="text-sm font-bold text-green-400">{((metrics?.totalWeight || 0) * 0.6).toFixed(0)} T</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-[hsl(215,28%,14%)] rounded-lg">
                  <div className="flex items-center gap-2">
                    <ArrowDownRight className="w-4 h-4 text-[hsl(24.6,95%,53%)]" />
                    <span className="text-sm text-white">Outbound</span>
                  </div>
                  <span className="text-sm font-bold text-[hsl(24.6,95%,53%)]">{((metrics?.totalWeight || 0) * 0.4).toFixed(0)} T</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-[hsl(215,28%,14%)] rounded-lg">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-[hsl(187,100%,42%)]" />
                    <span className="text-sm text-white">Trucks Weighed</span>
                  </div>
                  <span className="text-sm font-bold text-[hsl(187,100%,42%)]">{metrics?.dailyVisits || 0}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-[hsl(215,28%,14%)] rounded-lg">
                  <div className="flex items-center gap-2">
                    <Scale className="w-4 h-4 text-white" />
                    <span className="text-sm text-white">Avg Cycle</span>
                  </div>
                  <span className="text-sm font-bold text-white">{(metrics?.averageTime || 0).toFixed(1)} min</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
